import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'portfolistic-theme';
const TEXT_SIZE_KEY = 'portfolistic-text-size';
const THEMES = ['dark', 'light'];
const TEXT_SIZES = ['compact', 'normal', 'large', 'xl'];

function safeGetItem(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

function safeSetItem(key, value) {
  try { localStorage.setItem(key, value); } catch { /* private browsing */ }
}

function getInitialTheme() {
  // Priority: localStorage > OS preference > light (default)
  const stored = safeGetItem(STORAGE_KEY);
  if (stored && THEMES.includes(stored)) return stored;
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

// Light theme overrides (applied as CSS custom properties)
const LIGHT_OVERRIDES = {
  '--color-surface': '#f5f5f5',
  '--color-surface-dark': '#e5e5e5',
  '--color-surface-light': '#ffffff',
  '--color-surface-lighter': '#fafafa',
  '--color-surface-glass': 'rgba(0, 0, 0, 0.03)',
  '--color-surface-glass-border': 'rgba(0, 0, 0, 0.08)',
  '--color-text-primary': '#0a0a0a',
  '--color-text-secondary': '#404040',
  '--color-text-tertiary': '#737373',
  '--color-text-disabled': '#a3a3a3',
  '--color-text-inverse': '#ffffff',
  '--shadow-glass-sm': '0 2px 8px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
  '--shadow-glass': '0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
  '--shadow-glass-lg': '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
  '--shadow-glass-xl': '0 12px 48px rgba(0, 0, 0, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
  '--gradient-dark': 'linear-gradient(135deg, #f5f5f5 0%, #e5e5e5 100%)',
  '--gradient-glass': 'linear-gradient(135deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.04) 100%)',
};

// Dark theme values (the defaults in index.css, stored here for resetting)
const DARK_DEFAULTS = {
  '--color-surface': '#1a1a1a',
  '--color-surface-dark': '#0a0a0a',
  '--color-surface-light': '#2a2a2a',
  '--color-surface-lighter': '#3a3a3a',
  '--color-surface-glass': 'rgba(255, 255, 255, 0.05)',
  '--color-surface-glass-border': 'rgba(255, 255, 255, 0.1)',
  '--color-text-primary': '#ffffff',
  '--color-text-secondary': '#e5e5e5',
  '--color-text-tertiary': '#a3a3a3',
  '--color-text-disabled': '#737373',
  '--color-text-inverse': '#0a0a0a',
  '--shadow-glass-sm': '0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
  '--shadow-glass': '0 4px 16px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
  '--shadow-glass-lg': '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
  '--shadow-glass-xl': '0 12px 48px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
  '--gradient-dark': 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
  '--gradient-glass': 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
};

function applyTextSize(size) {
  const root = document.documentElement;
  if (size && size !== 'normal') {
    root.setAttribute(`data-text-size`, size);
  } else {
    root.removeAttribute('data-text-size');
  }
}

function applyTheme(theme) {
  const root = document.documentElement;
  const overrides = theme === 'light' ? LIGHT_OVERRIDES : DARK_DEFAULTS;
  for (const [prop, value] of Object.entries(overrides)) {
    root.style.setProperty(prop, value);
  }
  // Set data attribute for CSS selectors
  root.setAttribute('data-theme', theme);
  // Set color-scheme for native browser elements (scrollbars, form controls)
  root.style.colorScheme = theme;
  // Update body background
  document.body.style.backgroundColor = theme === 'light' ? '#f8f9fa' : '#0a0a0a';
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);
  const [textSize, setTextSizeState] = useState(() => {
    const stored = safeGetItem(TEXT_SIZE_KEY);
    return TEXT_SIZES.includes(stored) ? stored : 'normal';
  });

  // Apply theme on mount and changes
  useEffect(() => {
    applyTheme(theme);
    safeSetItem(STORAGE_KEY, theme);
  }, [theme]);

  // Apply text size on mount and changes
  useEffect(() => {
    applyTextSize(textSize);
    safeSetItem(TEXT_SIZE_KEY, textSize);
  }, [textSize]);

  // Listen for OS preference changes
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      const stored = safeGetItem(STORAGE_KEY);
      if (!stored) {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const setTheme = useCallback((newTheme) => {
    if (THEMES.includes(newTheme)) {
      setThemeState(newTheme);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const setTextSize = useCallback((size) => {
    if (TEXT_SIZES.includes(size)) {
      setTextSizeState(size);
    }
  }, []);

  const isDark = theme === 'dark';

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, isDark, textSize, setTextSize, TEXT_SIZES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}

export default useTheme;
